import { useState, useEffect } from "react";
import {
  Send,
  Paperclip,
  Menu,
  Bell,
  Search,
  User,
  FileText,
  ExternalLink,
  Shield,
  Lock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function MobileDemo() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setStep(prev => (prev < 4 ? prev + 1 : prev));
    }, 1500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-900 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl overflow-hidden flex flex-col">
      {/* Notch / Status Bar */}
      <div className="h-[32px] bg-gray-800 w-full absolute top-0 left-0 z-20 rounded-t-[1.5rem]">
        <div className="h-[18px] w-[100px] bg-black absolute top-0 left-1/2 -translate-x-1/2 rounded-b-[1rem]"></div>
      </div>

      {/* App Header */}
      <div className="pt-10 pb-3 px-4 bg-[#0f172a] text-white flex items-center justify-between z-10 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center text-[10px] font-bold">
            M
          </div>
          <span className="font-semibold text-sm">Minha Empresa</span>
        </div>
        <div className="flex gap-3">
          <Search className="w-4 h-4 text-gray-400" />
          <Bell className="w-4 h-4 text-gray-400" />
          <Avatar className="w-5 h-5">
            <AvatarImage src="https://github.com/shadcn.png" />
            <AvatarFallback>CN</AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Security Badge */}
      <div className="bg-emerald-900/30 border-b border-emerald-500/20 py-1.5 px-4 flex items-center justify-center gap-1.5">
        <Lock className="w-3 h-3 text-emerald-500" />
        <span className="text-[10px] font-medium text-emerald-500">
          Ambiente Seguro | 100% Offline
        </span>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-slate-50 dark:bg-slate-900 p-4 overflow-y-auto flex flex-col gap-4 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:16px_16px]"></div>

        {/* User Message */}
        <div
          className={`flex justify-end transition-all duration-500 ${step >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
          <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 pb-3 pt-2 max-w-[90%] shadow-sm text-xs leading-relaxed">
            Quais são os procedimentos para troca do filtro da máquina XY200?
            <div className="text-[10px] text-blue-200 mt-1 text-right">
              14:32
            </div>
          </div>
        </div>

        {/* AI Typing Indicator */}
        {step === 2 && (
          <div className="flex justify-start animate-pulse">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex gap-1">
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></div>
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
            </div>
          </div>
        )}

        {/* AI Response */}
        {step >= 3 && (
          <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex justify-start">
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[95%] shadow-sm text-xs leading-relaxed">
                <p className="mb-2 font-medium text-slate-900 dark:text-white">
                  Para trocar o filtro da máquina XY200, siga os passos:
                </p>
                <ol className="list-decimal pl-4 mb-3 text-slate-600 dark:text-slate-300">
                  <li>Desligue a máquina e isole a fonte de energia.</li>
                  <li>Remova a tampa de acesso e libere o compartimento.</li>
                  <li>Retire o filtro antigo e insira o novo.</li>
                  <li>Recoloque a tampa e teste o funcionamento.</li>
                </ol>

                <div className="flex flex-wrap gap-2 mt-4 pt-4 dark:border-slate-700">
                  <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2 py-1 rounded text-[10px] font-medium border border-blue-100 dark:border-blue-800">
                    <FileText className="w-3 h-3" />
                    Manual Op. XY200
                  </div>
                  <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2 py-1 rounded text-[10px] font-medium border border-blue-100 dark:border-blue-800">
                    <ExternalLink className="w-3 h-3" />
                    POP Manutenção p.15
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 px-1">
              <Shield className="w-3 h-3 text-emerald-500" />
              <span className="text-[10px] text-slate-400">
                Fonte Citada • Logs Auditados
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="bg-white dark:bg-slate-900 p-3 border-t border-slate-200 dark:border-slate-800 z-10">
        <div className="relative flex items-center">
          <Paperclip className="w-4 h-4 text-slate-400 absolute left-3" />
          <div className="w-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs py-2.5 pl-9 pr-3 rounded-full border border-transparent focus:border-blue-500 transition-colors">
            Faça sua pergunta...
          </div>
          <div className="absolute right-1.5 bg-blue-600 p-1.5 rounded-full text-white">
            <Send className="w-3 h-3" />
          </div>
        </div>
        <div className="flex justify-center gap-4 mt-3">
          <div className="flex items-center gap-1.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
            Roteiro Interno
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            Conhecimento Geral
          </div>
        </div>
      </div>

      {/* Home Indicator */}
      <div className="h-[20px] bg-white dark:bg-slate-900 w-full flex justify-center items-start pt-2">
        <div className="w-[100px] h-[4px] bg-slate-300 dark:bg-slate-700 rounded-full"></div>
      </div>
    </div>
  );
}
